<template>
  <v-card v-if="editMode" class="metric" outlined :ripple="false">
    <div class="mt-4 mx-8 d-flex align-center">
      <span
        class="subtitle-2 font-weight-bold blue-grey--text text--darken-3"
        >{{ $t(`impacts.${metric.name}`) }}</span
      >
      <v-icon
        class="ml-auto cursor-pointer"
        color="blue-grey darken-3"
        data-testid="delete-metric-button"
        size="14"
        @click="$emit('delete', metric)"
        >mdi-close</v-icon
      >
    </div>
    <div class="mt-6 mx-8 d-flex" :class="required ? 'mb-2' : 'mb-8'">
      <v-row>
        <template v-if="!isFinancialMetric">
          <v-col :cols="metricUnits.length ? 4 : 8" class="pr-2">
            <v-text-field
              v-model="amount"
              dense
              data-testid="metric-amount-field"
              maxlength="10"
              outlined
              :hide-details="!required"
              :label="$t('common.amount')"
              :rules="rules.amount"
              @keydown="keyPress"
              @input="onInput"
            />
          </v-col>
          <v-col v-if="metricUnits.length" cols="4" class="pr-2 pl-0">
            <v-select
              dense
              outlined
              class="units"
              data-testid="metric-unit-selector"
              :hide-details="!required"
              :items="metricUnits"
              :label="$t('common.unit')"
              :value="metricUnit"
              :menu-props="{ offsetY: true }"
              :rules="rules.unit"
              @change="handleUnitChange"
            />
          </v-col>
        </template>

        <v-col v-else cols="8" class="pr-2">
          <MoneyInput
            allowDecimals
            data-testid="metric-currency-field"
            :label="$t('common.amount')"
            :currencyCode="revCurrency"
            :currencySymbol="currencySymbol"
            :value="amount"
            :required="required"
            @onInput="handleMoneyInput"
            @onBlur="$emit('onBlur')"
          />
          <p
            v-if="isDifferentCurrency && amount > 0"
            class="label grey--text ma-0 pa-0 mt-1"
            :class="{ 'mt-n6': required }"
            datatid="money-question-exchange"
          >
            <span>{{ organizationCurrencySymbol }}</span>
            <span>{{ exchangeValue.toLocaleString(language) }}</span>
            <span> {{ organizationCurrency }}</span>
          </p>
        </v-col>
        <v-col cols="4" class="pr-2 pl-0">
          <v-select
            dense
            data-testid="metric-frecuency-selector"
            outlined
            :hide-details="!required"
            :items="recurrencyOptions"
            :value="metric.frequency || DEFAULT_FREQUENCY"
            :label="$t('common.recurrence')"
            :menu-props="{ offsetY: true }"
            :rules="rules.recurrence"
            @change="handleRecurrenceChange"
          />
        </v-col>
      </v-row>
    </div>
  </v-card>

  <v-card v-else class="metric py-4 pl-6 pr-4" outlined :ripple="false">
    <div class="d-flex metric-header">
      <div
        class="blue-grey--text text--darken-3 subtitle-2 font-weight-bold metric-title"
      >
        {{ $t(`impacts.${metric.name}`) }}
      </div>
      <v-icon
        v-if="metricIcon"
        class="metric-icon ml-auto"
        :class="metricIcon.bgColor"
        :color="metricIcon.color"
        size="10"
        >{{ metricIcon.icon }}</v-icon
      >
    </div>
    <div class="font-size-20 blue-grey--text text--darken-3 mt-1">
      {{ metricTextValue }}
    </div>
    <div class="caption blue-grey--text text--darken-1 mt-1">
      <span v-if="isFinancialMetric">{{ valueInGlobalCurrency }}</span
      >{{ metricFrequency }}
    </div>
  </v-card>
</template>

<script>
import _ from 'lodash'

import currencies from '@/locales/currencies.json'

import MoneyInput from '@/components/Rev/Review/MoneyInput.vue'

export const TIME_UNITS = {
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
  weeks: 'weeks',
}

export const TIME_RECURRENCES = {
  hours: 'hours',
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  annually: 'annually',
  one_time: 'one_time',
}

const DEFAULT_FREQUENCY = 'one_time'

export default {
  name: 'Metric',

  components: {
    MoneyInput,
  },

  props: {
    metric: { type: Object, default: () => {} },
    isImpact: Boolean,
    revCurrency: { type: String, required: true },
    organizationCurrency: { type: String, required: true },
    exchangeRate: { type: Number, required: true },
    editMode: Boolean,
    required: Boolean,
  },

  data() {
    return {
      DEFAULT_FREQUENCY,
      amount: this.metric.value ?? null,
      language: navigator.language,
    }
  },

  watch: {
    allowHourlyRecurrence(currentValue) {
      if (!currentValue && this.metric.frequency === TIME_RECURRENCES.hours)
        this.handleRecurrenceChange(null)
    },
    allowDailyRecurrence(currentValue) {
      if (!currentValue && this.metric.frequency === TIME_RECURRENCES.daily)
        this.handleRecurrenceChange(null)
    },
    allowWeeklyRecurrence(currentValue) {
      if (!currentValue && this.metric.frequency === TIME_RECURRENCES.weekly)
        this.handleRecurrenceChange(null)
    },
    allowMonthlyRecurrence(currentValue) {
      if (!currentValue && this.metric.frequency === TIME_RECURRENCES.monthly)
        this.handleRecurrenceChange(null)
    },
    allowAnnuallyRecurrence(currentValue) {
      if (!currentValue && this.metric.frequency === TIME_RECURRENCES.annually)
        this.handleRecurrenceChange(null)
    },
  },

  computed: {
    recurrencyOptions() {
      return _.compact([
        {
          value: 'one_time',
          text: this.$t('rev.time_tracking.units.one_time_only'),
        },
        this.isImpact &&
          this.allowHourlyRecurrence && {
            value: TIME_RECURRENCES.hours,
            text: this.$t('impact_recurrence.hours'),
          },
        this.allowDailyRecurrence && {
          value: 'daily',
          text: this.$t('rev.time_tracking.units.daily'),
        },
        this.allowWeeklyRecurrence && {
          value: TIME_RECURRENCES.weekly,
          text: this.$t('rev.time_tracking.units.weekly'),
        },
        this.allowMonthlyRecurrence && {
          value: TIME_RECURRENCES.monthly,
          text: this.$t('rev.time_tracking.units.monthly'),
        },
        this.allowAnnuallyRecurrence && {
          value: TIME_RECURRENCES.annually,
          text: this.$t('rev.time_tracking.units.annually'),
        },
      ])
    },

    valueOrUnitEmpty() {
      return !this.metric.value || !this.metric.unit
    },

    isTimeUnit() {
      const timeUnits = [
        TIME_UNITS.minutes,
        TIME_UNITS.hours,
        TIME_UNITS.days,
        TIME_UNITS.weeks,
      ]
      return timeUnits.includes(this.metric.unit)
    },

    allowHourlyRecurrence() {
      return (
        this.valueOrUnitEmpty ||
        !this.isTimeUnit ||
        this.isValidValue(TIME_UNITS.minutes, 60)
      )
    },

    allowDailyRecurrence() {
      return (
        this.valueOrUnitEmpty ||
        !this.isTimeUnit ||
        this.isValidValue(TIME_UNITS.minutes, 60 * 24) ||
        this.isValidValue(TIME_UNITS.hours, 24)
      )
    },

    allowWeeklyRecurrence() {
      return (
        this.valueOrUnitEmpty ||
        !this.isTimeUnit ||
        this.isValidValue(TIME_UNITS.minutes, 60 * 24 * 7) ||
        this.isValidValue(TIME_UNITS.hours, 24 * 7) ||
        this.isValidValue(TIME_UNITS.days, 7)
      )
    },
    allowMonthlyRecurrence() {
      return (
        this.valueOrUnitEmpty ||
        !this.isTimeUnit ||
        this.isValidValue(TIME_UNITS.minutes, 60 * 24 * 30) ||
        this.isValidValue(TIME_UNITS.hours, 24 * 30) ||
        this.isValidValue(TIME_UNITS.days, 30) ||
        this.isValidValue(TIME_UNITS.weeks, 4)
      )
    },
    allowAnnuallyRecurrence() {
      return (
        this.valueOrUnitEmpty ||
        !this.isTimeUnit ||
        this.isValidValue(TIME_UNITS.minutes, 60 * 24 * 365) ||
        this.isValidValue(TIME_UNITS.hours, 24 * 365) ||
        this.isValidValue(TIME_UNITS.days, 365) ||
        this.isValidValue(TIME_UNITS.weeks, 52)
      )
    },

    currencySymbol() {
      return _.get(currencies[this.revCurrency], 'symbol_native', '')
    },

    organizationCurrencySymbol() {
      return _.get(currencies[this.organizationCurrency], 'symbol_native', '')
    },

    isDifferentCurrency() {
      return this.organizationCurrency !== this.revCurrency
    },

    exchangeValue() {
      return Math.round(parseFloat(this.amount) / this.exchangeRate)
    },

    metricUnits() {
      return this.metric.units.map(unit => ({
        value: unit.name,
        text: this.$t(`impact_units.${unit.name}`),
      }))
    },

    isFinancialMetric() {
      return this.metricUnits?.find(
        unit => unit.value === this.$constants.metricUnitTypes.currency
      )
    },

    metricUnit() {
      return (
        this.metric.unit ??
        (this.metricUnits.length === 1 ? this.metricUnits[0].value : null)
      )
    },

    metricIcon() {
      const unit = this.metric.units.find(
        unit => unit.name === this.metric.unit
      )
      const magnitude = unit?.magnitude ?? unit?.name ?? null
      switch (magnitude) {
        case this.$constants.metricUnitTypes.energy:
          return {
            icon: 'mdi-flash',
            color: 'yellow',
            bgColor: 'yellow lighten-2',
          }
        case this.$constants.metricUnitTypes.currency:
          return {
            icon: 'mdi-currency-usd',
            color: 'green',
            bgColor: 'green lighten-2',
          }
        case this.$constants.metricUnitTypes.time:
          return {
            icon: 'mdi-clock-outline',
            color: 'teal',
            bgColor: 'teal lighten-2',
          }
        case this.$constants.metricUnitTypes.weight:
          return {
            icon: 'mdi-weight-kilogram',
            color: 'red',
            bgColor: 'red lighten-2',
          }
        case this.$constants.metricUnitTypes.volume:
          return {
            icon: 'mdi-waves',
            color: 'blue',
            bgColor: 'blue lighten-2',
          }
        case this.$constants.metricUnitTypes.percentage:
          return {
            icon: 'mdi-chart-donut',
            color: 'purple',
            bgColor: 'purple lighten-2',
          }
        default:
          return {
            icon: 'mdi-pound',
            color: 'orange',
            bgColor: 'orange lighten-2',
          }
      }
    },

    metricTextValue() {
      if (!this.metric.value) return '-'

      const formattedValue = Number(this.metric.value).toLocaleString(
        navigator.language
      )

      if (!this.metric.unit) return formattedValue

      if (this.metric.unit === this.$constants.metricUnitTypes.currency) {
        return `${this.currencySymbol} ${formattedValue} ${this.revCurrency}`
      }

      if (this.metric.unit === this.$constants.metricUnitTypes.percentage) {
        return `${formattedValue} %`
      }

      const unit = this.$t(`impact_units.${this.metric.unit}`)

      return `${formattedValue} ${unit}`
    },

    valueInGlobalCurrency() {
      if (!this.isDifferentCurrency || !this.exchangeValue) return ''

      return `${
        this.organizationCurrencySymbol
      } ${this.exchangeValue.toLocaleString(navigator.language)} ${
        this.organizationCurrency
      } / `
    },

    metricFrequency() {
      if (!this.metric.frequency) return '-'
      if (this.metric.frequency === 'one_time')
        return this.$t('impact_recurrence.one_time_only')

      return this.$t(`impact_recurrence.${this.metric.frequency}`)
    },

    rules() {
      return {
        amount: this.required
          ? [
              field =>
                Boolean(field) ||
                this.$t('validation.form.this_field_is_required'),
            ]
          : [],
        unit: this.required
          ? [
              field =>
                Boolean(field?.length) ||
                this.$t('validation.form.this_field_is_required'),
            ]
          : [],
        recurrence: this.required
          ? [
              field =>
                Boolean(field) ||
                this.$t('validation.form.this_field_is_required'),
            ]
          : [],
      }
    },
  },

  methods: {
    keyPress(event) {
      const dotExist = String(this.amount).match(/\./g)

      const keyCode = event.keyCode ?? event.which
      if (keyCode === this.$constants.KEY_CODES.BACKSPACE) return
      if (keyCode == this.$constants.KEY_CODES.TAB) return

      const isNumber =
        (keyCode >= this.$constants.KEY_CODES.ZERO &&
          keyCode <= this.$constants.KEY_CODES.NINE) ||
        (keyCode >= this.$constants.KEY_CODES.NUM_PAD_ZERO &&
          keyCode <= this.$constants.KEY_CODES.NUM_PAD_NINE) ||
        (keyCode === this.$constants.KEY_CODES.DOT && !dotExist) ||
        (keyCode >= this.$constants.KEY_CODES.LEFT &&
          keyCode <= this.$constants.KEY_CODES.DOWN)
      if (!isNumber) event.preventDefault()
    },

    onInput(value) {
      const parsedValue = _.round(parseFloat(value), 2)

      this.$emit('change', {
        ...this.metric,
        value: isNaN(parsedValue) ? null : parsedValue,
      })
    },

    handleMoneyInput(value) {
      this.amount = value ? _.round(value, 2) : null
      this.$emit('change', {
        ...this.metric,
        value: this.amount,
        unit: 'currency',
      })
    },

    handleRecurrenceChange(recurrence) {
      this.$emit('change', { ...this.metric, frequency: recurrence })
    },

    handleUnitChange(unit) {
      this.$emit('change', { ...this.metric, unit })
    },

    isValidValue(unit, maxValue) {
      return (
        this.metric.unit === TIME_UNITS[unit] && this.metric.value <= maxValue
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.metric {
  &::before {
    background-color: transparent;
  }
  &:hover {
    border: 1px solid $color-blue-grey-lighten1 !important;
    transition: 0.2s ease;
  }
}

.metric-header {
  white-space: nowrap;
}

.metric-title {
  text-overflow: ellipsis;
  overflow: hidden;
}
.metric-icon {
  width: 20px;
  height: 20px;
  border-radius: 10px;
  flex-shrink: 0;
}
</style>
